import { Component } from "react";
import gsap from "gsap";
import CardDefault from '../../../../../../assets/games/russianPoker/rp_sprite_card.webp';
import riverBackCard from "../../../../../../assets/games/russianPoker/river_sprite_card.webp";
import { Howl, Howler } from "howler";

import cardsdropping_snd from '../../../../../../assets/sounds/carddroping.mp3';
import cardsshuffle from "../../../../../../assets/sounds/shuffle-cards.mp3";

const DEFAULT_FRAME = [689, 0, 53, 73];
const safeFrame = (frame) => (Array.isArray(frame) && frame.length === 4 ? frame : DEFAULT_FRAME);

/**
 * One shared motion language for every card action (deal / draw / shuffle /
 * reveal / restore). Previously each method used a different ease
 * ("none", "power1.inOut", nothing at all) which is a big part of why the
 * table felt inconsistent card-to-card. Landing always eases out with a
 * touch of overshoot so a card visibly "settles" instead of just stopping.
 */
const EASE_LIFT = "power1.in";       // leaving the deck / going back to deck
const EASE_FLIGHT = "power2.out";    // travelling across the table
const EASE_SETTLE = "back.out(.35)"; // final landing overshoot-and-settle
const GPU = { force3D: true };       // always request a GPU-composited transform

/**
 * CSS/DOM replacement for the old Konva <Sprite> based DealerCard.
 * Same rationale as Card.js: GSAP now tweens a real DOM node's CSS
 * transform (translate3d/scale/rotate) instead of forcing Konva to
 * rasterize the whole layer to canvas on every frame. Method names and
 * call signatures are kept identical to the original so DealerCardsContainer.js
 * doesn't need to change how it drives this component.
 */
class DealerCard extends Component {
    constructor(props) {
        super(props);
        this.logoBackSideCard = this.props.siteid == "rp1" ? riverBackCard : CardDefault;
        this.cardStyle = this.logoBackSideCard;

        this.state = {
            sheetW: 0,
            sheetH: 0,
            ready: false,
            visible: false,
        };

        this.cardDropPositionX = null;
        this.cardDropPositionY = null;
        this.cardContext = null;
        this.isAnimating = false; // true while a dedicated deal/draw/reveal timeline owns this node
        this.cardRef = null; // DOM node (replaces this.imageNode)

        this.handleResize = this.handleResize.bind(this);
        this.handleResize();
    }

    componentDidMount() {
        this.logoBackSideCard = this.props.siteid == "rp1" ? riverBackCard : CardDefault;
        this.loadImage(this.logoBackSideCard);

        window.addEventListener("resize", this.handleResize);

        Howler.autoUnlock = true;
        this.carddropsound = new Howl({ src: [cardsdropping_snd], html5: false, preload: true });
        this.cardsshuffleSound = new Howl({ src: [cardsshuffle], html5: false, preload: true });
    }

    playSound = (soundInstance) => {
        if (this.props.mutesounds) return;
        this.soundSuspendedChecker();
        if (soundInstance) {
            soundInstance.stop();
            soundInstance.play();
        }
    };

    soundSuspendedChecker = () => {
        if (document.visibilityState === 'visible' && Howler.ctx && (Howler.ctx.state === 'suspended' || Howler.ctx.state !== 'running')) {
            Howler.ctx.resume();
        }
        Howler._audioUnlocked = true;
    };

    componentDidUpdate(prevProps) {
        if (this.cardStyle !== this.props.cardStyle) {
            this.loadImage(this.props.cardStyle);
            this.cardStyle = this.props.cardStyle;
        }
        if (this.props.x !== prevProps.x) {
            this.changeCardPosition();
        }
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.handleResize);
        if (this.image) {
            this.image.removeEventListener("load", this.handleLoad);
        }
        if (this.carddropsound) {
            this.carddropsound.stop();
            this.carddropsound.unload();
        }
        if (this.cardsshuffleSound) {
            this.cardsshuffleSound.stop();
            this.cardsshuffleSound.unload();
        }
        if (this.cardContext) {
            this.cardContext.revert();
        }
    }

    handleResize() {
        if (this.cardRef) {
            // Was a gsap.to(..., {duration: 0.001}) — a real tween, which
            // could start mid-flight while animateCard/DrawanimateCard/etc.
            // were still animating the same node and silently overwrite or
            // race with them (a big source of stutter on orientation change
            // / mobile toolbar show-hide). gsap.set() is instantaneous and
            // doesn't enter the tween queue at all, so it can't conflict.
            gsap.set(this.cardRef, {
                x: this.props.x,
                y: this.props.y,
                scaleX: this.props.scaleX,
                scaleY: this.props.scaleY,
            });
        }

        if (
            (navigator.userAgentData && navigator.userAgentData.platform && navigator.userAgentData.platform === "Windows") ||
            (navigator.userAgent.indexOf("Windows") !== -1) ||
            (navigator.platform.includes("Mac") && !("ontouchend" in document || navigator.maxTouchPoints > 0))
        ) {
            this.cardDropPositionX = window.innerWidth / 2 + 800;
            this.cardDropPositionY = -(window.innerHeight / 2 - 550);
        } else {
            if (window.innerWidth > window.innerHeight) {
                this.cardDropPositionX = window.innerWidth / 2 + 200;
                this.cardDropPositionY = -(window.innerHeight / 2) + 450;
            } else {
                this.cardDropPositionX = window.innerWidth / 2 + 400;
                this.cardDropPositionY = window.innerHeight / 2 - 400;
            }
        }
    }

    changeCardPosition() {
        if (!this.cardRef) return;
        if (this.isAnimating) return;
        gsap.killTweensOf(this.cardRef);
        this.cardContext = gsap.context(() => {
            gsap.to(this.cardRef, { x: this.props.x, y: this.props.y, duration: 0.45, ease: EASE_FLIGHT, ...GPU });
        }, this.cardRef);
    }

    loadImage(img) {
        this.image = new window.Image();
        this.image.src = img;
        this.image.addEventListener("load", this.handleLoad);
    }

    handleLoad = () => {
        this.setState({
            sheetW: this.image.naturalWidth,
            sheetH: this.image.naturalHeight,
            ready: true,
        });
    };

    showPreviousDealercards() {
        this.props.UpdateCard({ x: this.props.x, y: this.props.y });
        if (!this.cardRef) return;

        this.isAnimating = true;
        gsap.killTweensOf(this.cardRef);
        this.setState({ visible: true });
        gsap.fromTo(
            this.cardRef,
            { x: this.cardDropPositionX, y: this.cardDropPositionY, scaleX: 0.3, scaleY: 0.3, opacity: 0.6, rotation: 0 },
            {
                x: this.props.x, y: this.props.y, scaleX: this.props.scaleX, scaleY: this.props.scaleY,
                opacity: 1, rotation: 0,
                duration: 0.55, ease: EASE_SETTLE, ...GPU,
                onComplete: () => { this.isAnimating = false; this.afterFinish(); },
            }
        );
    }

    animateCard() {
        this.props.UpdateCard({ x: this.props.x, y: this.props.y });
        if (!this.cardRef) return;

        this.isAnimating = true;
        gsap.killTweensOf(this.cardRef);
        this.setState({ visible: true });
        gsap.fromTo(
            this.cardRef,
            { x: this.cardDropPositionX, y: this.cardDropPositionY, scaleX: 0.3, scaleY: 0.3, opacity: 0.4, rotation: 0 },
            {
                x: this.props.x, y: this.props.y, scaleX: this.props.scaleX, scaleY: this.props.scaleY,
                opacity: 1, rotation: 0,
                duration: 0.55, ease: EASE_SETTLE, ...GPU,
                onComplete: () => { this.isAnimating = false; this.afterFinish(); },
            }
        );
    }

    afterFinish() {
        this.props.XY({ index: -1, text: this.props.text, x: this.props.x, y: this.props.y });
    }

    revealdealerMaskCard = () => {
        if (!this.cardRef) return;
        this.isAnimating = true;
        gsap.killTweensOf(this.cardRef);
        // A real card flip: squash to a sliver on the vertical axis (the
        // "edge-on" moment), then unfold back out to full size once the
        // face texture has swapped. One timeline, one ease family, so it
        // reads as a single continuous motion instead of two disconnected
        // tweens fighting for the same scaleX/scaleY properties.
        const tl = gsap.timeline({ onComplete: () => this.draw() });
        tl.to(this.cardRef, { scaleX: 0.02, duration: 0.18, ease: "power1.in", ...GPU });
        tl.to(this.cardRef, { scaleX: this.props.scaleX, duration: 0.3, ease: EASE_SETTLE, ...GPU, onComplete: () => { this.isAnimating = false; } });
    };

    draw() {
        this.props.XY({ index: this.props.index, text: this.props.text, x: this.props.x, y: this.props.y });
    }

    DrawanimateCard(data) {
        if (!this.cardRef) return;
        this.isAnimating = true;
        gsap.killTweensOf(this.cardRef);
        // Same fix as Card.js: no spin on draw, rotation held fixed at 0
        // (dealer's row is flat, not fanned) through the whole timeline.
        const tl = gsap.timeline({ onComplete: () => { this.isAnimating = false; this.draw(); } });
        tl.fromTo(this.cardRef, { x: this.props.x, y: this.props.y, scaleX: 1, scaleY: 1, opacity: 1, rotation: 0 }, {
            x: this.cardDropPositionX, y: this.cardDropPositionY, scaleX: 0.7, scaleY: 0.5, rotation: 0, duration: 0.4,
            ease: EASE_LIFT, opacity: 0, ...GPU,
            onStart: () => this.playSound(this.carddropsound),
        });
        tl.fromTo(this.cardRef, { x: this.cardDropPositionX, y: this.cardDropPositionY, scaleX: 0.2, scaleY: 0.2, opacity: 0, rotation: 0 }, {
            x: this.props.x, y: this.props.y, scaleX: this.props.scaleX, scaleY: this.props.scaleY, rotation: 0, duration: 0.45,
            ease: EASE_SETTLE, opacity: 1, ...GPU,
            onStart: () => this.playSound(this.carddropsound),
        }, "-=0.03");
    }

    SuffleCard(data) {
        if (!this.cardRef) return;
        gsap.killTweensOf(this.cardRef);
        gsap.fromTo(this.cardRef, { x: data.x, y: data.y - 14, opacity: 0.5, rotation: 0 }, {
            x: this.props.x, y: this.props.y, rotation: 0, duration: 0.45, ease: EASE_SETTLE, opacity: 1, ...GPU,
            onStart: () => this.playSound(this.cardsshuffleSound),
            onComplete: () => { this.isAnimating = false; this.Uc(data); },
        });
    }

    Uc(data) {
        this.props.XY({ index: this.props.index, text: this.props.text, x: data.x, y: data.y });
    }

    endGame = () => {
        if (this.cardContext) {
            this.cardContext.revert();
        }
    };

    render() {
        const [fx, fy, fw, fh] = safeFrame(this.props.frame);
        const { sheetW, sheetH, ready, visible } = this.state;

        const style = {
            position: "absolute",
            top: 0,
            left: 0,
            width: fw,
            height: fh,
            // Centered origin: scaling a card that's simultaneously being
            // translated used to pivot from its top-left corner ("0 0"),
            // which reads as a slide/warp rather than a clean grow-in-place.
            transformOrigin: "50% 50%",
            opacity: ready ? this.props.alpha : 0,
            visibility: visible ? "visible" : "hidden",
            backgroundImage: ready ? `url(${this.cardStyle})` : "none",
            backgroundPosition: `-${fx}px -${fy}px`,
            backgroundSize: `${sheetW}px ${sheetH}px`,
            backgroundRepeat: "no-repeat",
            willChange: "transform, opacity",
            transform: "translate3d(0,0,0) scale3d(0.15,0.15,1)",
        };

        return (
            <div
                className="rp-dealer-card"
                ref={(node) => { this.cardRef = node; }}
                style={style}
            />
        );
    }
}

export default DealerCard;
